import type { NavModuleKey } from '@/config/navigationModules';

/** Pack key → nav module to hide when the pack is not installed. */
export const PACK_REQUIRED_MODULES: Partial<Record<string, NavModuleKey[]>> = {
  manufacturing_core: ['mfg'],
  education_core: ['edu'],
};

/**
 * Paths that require Manufacturing Core even when listed under other modules (e.g. Sales).
 * Exact key match against menu item `key`.
 */
export const MANUFACTURING_CORE_PATHS = new Set<string>([
  '/mfg/operations',
  '/mfg/control-tower',
  '/mfg/factory-floor',
  '/mfg/workstation-control-center',
  '/mfg/work-orders',
  '/mfg/production-requests',
  '/mfg/work-order-operations',
  '/mfg/work-order-operations/eod-close',
  '/mfg/route-sheet-register',
  '/mfg/traceability-matrix',
  '/mfg/boms',
  '/mfg/workstations',
  '/mfg/calendar',
  '/mfg/capacity-planning',
  '/mfg/material-audit',
  '/mfg/master-schedule',
]);

/**
 * Oil & Gas Fabrication–specific nav paths (hidden when oil_gas_fabrication is not installed,
 * even if Manufacturing Core remains).
 */
export const OIL_GAS_FABRICATION_PATHS = new Set<string>([
  '/mfg/route-sheet-register',
  '/mfg/traceability-matrix',
  '/manufacturing/heat-traceability',
  '/manufacturing/digital-traveler',
  '/quality/mtc',
  '/quality/ndt',
  '/qms/mtc',
  '/qms/ndt',
]);

export const PACK_KEY_MANUFACTURING_CORE = 'manufacturing_core';
export const PACK_KEY_OIL_GAS_FABRICATION = 'oil_gas_fabrication';
export const PACK_KEY_EDUCATION_CORE = 'education_core';

/**
 * Modules Education tenants do not use (hidden when education_core is installed).
 * Growth & Revenue (`sales`) is replaced by the Education top-nav module.
 */
export const EDUCATION_HIDDEN_MODULES: NavModuleKey[] = ['sales', 'epc', 'qms'];

export function isEducationPath(path: string): boolean {
  return path === '/education' || path.startsWith('/education/');
}

/** Growth, EPC, QMS, and SCM quality/inspection surfaces — not used on Education. */
export function isEducationHiddenPath(path: string): boolean {
  if (!path) return false;
  if (path === '/growth' || path.startsWith('/growth/')) return true;
  if (path === '/sales' || path.startsWith('/sales/')) return true;
  if (path === '/crm' || path.startsWith('/crm/')) return true;
  if (path === '/epc' || path.startsWith('/epc/')) return true;
  if (path === '/qms' || path.startsWith('/qms/')) return true;
  if (path === '/quality' || path.startsWith('/quality/')) return true;
  if (path.startsWith('/scm/quality')) return true;
  return false;
}

/** EPC Commercial paths — visible when any EPC construction pack is installed. */
export const EPC_COMMERCIAL_PATHS = new Set<string>([
  '/epc/commercial',
  '/epc/commercial/prequalifications',
  '/epc/commercial/tenders',
  '/epc/commercial/bid-decisions',
  '/epc/commercial/awards',
  '/epc/commercial/contracts',
  '/epc/commercial/subcontracts',
  '/epc/commercial/subcontracts/new',
  '/epc/commercial/mobilizations',
  '/epc/commercial/control-tower',
]);

export const EPC_COMMERCIAL_PACK_KEYS = [
  'construction_core',
  'industrial_epc',
  'oil_gas_epc',
] as const;

export function isEpcCommercialPath(path: string): boolean {
  if (!path) return false;
  if (EPC_COMMERCIAL_PATHS.has(path)) return true;
  return path.startsWith('/epc/commercial/') || path === '/epc/ipc' || path.startsWith('/epc/ipc/');
}

export function hasInstalledPack(
  installedPackKeys: string[] | null | undefined,
  packKey: string
): boolean {
  if (installedPackKeys == null) return true; // fail-open for non-Education callers
  return installedPackKeys.includes(packKey);
}

/**
 * Pack keys used for nav gating.
 * Null means the list is unknown (loading, error, or missing). Other verticals stay
 * fail-open on null. Education does not: see applyPackModuleGates.
 */
export function installedPackKeysForNav(state: {
  isLoading: boolean;
  isError: boolean;
  installedPackKeys?: string[] | null;
}): string[] | null {
  if (state.isLoading || state.isError || state.installedPackKeys == null) return null;
  return state.installedPackKeys;
}

function withoutEducationModule(visible: Set<NavModuleKey>): Set<NavModuleKey> {
  if (!visible.has('edu')) return visible;
  const next = new Set(visible);
  next.delete('edu');
  return next;
}

export function applyPackModuleGates(
  visible: Set<NavModuleKey>,
  installedPackKeys: string[] | null | undefined
): Set<NavModuleKey> {
  // Education is fail-closed until education_core is positively present.
  // Other modules keep the existing fail-open behavior while the list is unknown.
  if (installedPackKeys == null) return withoutEducationModule(visible);
  const next = new Set(visible);
  for (const [packKey, modules] of Object.entries(PACK_REQUIRED_MODULES)) {
    if (!installedPackKeys.includes(packKey)) {
      for (const mod of modules || []) {
        next.delete(mod);
      }
    }
  }
  if (installedPackKeys.includes(PACK_KEY_EDUCATION_CORE)) {
    for (const mod of EDUCATION_HIDDEN_MODULES) {
      next.delete(mod);
    }
  }
  return next;
}

/** Whether a menu item path should be shown given installed packs. */
export function isNavPathAllowedByPacks(
  path: string,
  installedPackKeys: string[] | null | undefined
): boolean {
  if (isEducationPath(path)) {
    return (
      Array.isArray(installedPackKeys) && installedPackKeys.includes(PACK_KEY_EDUCATION_CORE)
    );
  }

  if (installedPackKeys == null) return true; // fail-open for Manufacturing, EPC, and other verticals
  if (!path || path === '/') return true;

  if (isEducationPath(path)) {
    return installedPackKeys.includes(PACK_KEY_EDUCATION_CORE);
  }

  if (
    installedPackKeys.includes(PACK_KEY_EDUCATION_CORE) &&
    isEducationHiddenPath(path)
  ) {
    return false;
  }

  if (isEpcCommercialPath(path)) {
    const hasEpcPack = EPC_COMMERCIAL_PACK_KEYS.some((k) => installedPackKeys.includes(k));
    if (!hasEpcPack) return false;
  }

  const needsOilGas = OIL_GAS_FABRICATION_PATHS.has(path);
  if (needsOilGas && !installedPackKeys.includes(PACK_KEY_OIL_GAS_FABRICATION)) {
    return false;
  }

  const needsMfgCore =
    MANUFACTURING_CORE_PATHS.has(path) ||
    path.startsWith('/mfg/') ||
    path.startsWith('/manufacturing/');
  if (needsMfgCore && !installedPackKeys.includes(PACK_KEY_MANUFACTURING_CORE)) {
    return false;
  }

  return true;
}
